import { BookingPriceRail } from '@/components/search/booking-price-rail';
import { DetailHeader } from '@/components/search/detail-header';
import { KeyHighlights } from '@/components/search/key-highlights';
import { type GroupMapItem, type PageMapSection, PageMapBar } from '@/components/search/page-map-bar';
import { PromoBanner } from '@/components/search/promo-banner';
import { RateResults } from '@/components/search/rate-results';
import { SearchBar } from '@/components/search/search-bar';
import { SearchResultsLayout } from '@/components/search/search-results-layout';
import { SectionHeader } from '@/components/search/section-header';
import { SiteVoucherNotice } from '@/components/search/site-voucher-notice';
import { RichContentRenderer } from '@/components/seo-content-renderer';
import { Skeleton } from '@/components/ui/skeleton';
import { GalleryPreview } from '@/components/web/gallery-preview';
import { NewsletterSection } from '@/components/web/home/newsletter-section';
import WebLayout from '@/components/web/layouts/web-layout';
import { MobileCartBottom } from '@/components/web/mobile-cart-bottom';
import { SuggestDates } from '@/components/web/suggest-dates';
import { useScrollSpy } from '@/hooks/use-scroll-spy';
import { formatDateForUrl, parseDateFromUrl } from '@/lib/date';
import { cn } from '@/lib/utils';
import { type BookingRate, type SearchPageProps } from '@/types/search';
import { Deferred, Head, router, usePage } from '@inertiajs/react';
import React, { useMemo, useState } from 'react';

export default function Search({ pageTitle, sites, selectedSite, services, vouchers }: SearchPageProps) {
  const page = usePage();
  const [error, setError] = useState<string | null>(null);

  const urlSearchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const rawDate = urlSearchParams.get('date') || '';
  const searchParams = {
    // mapping_code, not code: `code` is never selected by BookingSiteService, so
    // the old fallback was always undefined and this silently fell through to ''.
    site: urlSearchParams.get('site') || selectedSite?.mapping_code || '',
    date: parseDateFromUrl(rawDate), // Use SEO-friendly date format
    adults: parseInt(urlSearchParams.get('adults') || '2'),
    kid: parseInt(urlSearchParams.get('kid') || '0'),
    elderly: parseInt(urlSearchParams.get('elderly') || '0'),
  };

  React.useEffect(() => {
    if (typeof window !== 'undefined' && rawDate && rawDate !== searchParams.date) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('date', searchParams.date);
      window.history.replaceState({}, '', currentUrl.pathname + currentUrl.search);
    }
  }, [rawDate, searchParams.date]);

  const minPrice = useMemo(() => {
    if (!Array.isArray(services) || !services.length) return null;
    const prices = (services as BookingRate[]).flatMap((r) => r.Services.map((s) => s.Price)).filter((p) => p > 0);
    return prices.length ? Math.min(...prices) : null;
  }, [services]);

  // Build page sections for the page map bar
  const hasAbout = Boolean(selectedSite?.custom_content);
  const pageSections = useMemo<PageMapSection[]>(() => {
    const sections: PageMapSection[] = [{ id: 'packages', label: 'Gói dịch vụ' }];
    if (hasAbout) sections.push({ id: 'about', label: 'Về dịch vụ này' });
    return sections;
  }, [hasAbout]);

  // Build rate group items for the service group map bar
  const groupMapItems = useMemo<GroupMapItem[]>(() => {
    if (!Array.isArray(services) || !services.length) return [];
    const cats = [...new Set((services as BookingRate[]).map((r) => r.TypeName).filter((t): t is string => Boolean(t)))];
    return cats.map((cat, i) => ({ id: `rate-group-${i}`, label: cat }));
  }, [services]);

  // Page-level scroll-spy (packages, about)
  const activePageSection = useScrollSpy(
    pageSections.map((s) => s.id),
    { rootMargin: '-15% 0px -70% 0px' },
  );

  // Group-level scroll-spy (within packages)
  const activeGroupId = useScrollSpy(
    groupMapItems.map((g) => g.id),
    { rootMargin: '-15% 0px -65% 0px' },
  );

  const handleDateSelect = (newDate: string | null) => {
    const currentUrl = new URL(window.location.href);
    if (newDate) {
      currentUrl.searchParams.set('date', formatDateForUrl(newDate));
    } else {
      currentUrl.searchParams.delete('date');
    }
    router.get(currentUrl.pathname + currentUrl.search);
  };

  return (
    <WebLayout>
      <Head>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={`Tìm kiếm và đặt vé tại ${selectedSite?.name || 'các địa điểm du lịch'} với giá tốt nhất. Trải nghiệm dịch vụ chuyên nghiệp và tiện lợi.`}
        />
        <meta name="keywords" content="đặt vé, du lịch, VinWonders, giải trí, vui chơi" />

        {/* Open Graph tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={`Tìm kiếm và đặt vé tại ${selectedSite?.name || 'các địa điểm du lịch'}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={page.url} />

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'TouristAttraction',
            name: selectedSite?.name,
            description: `Dịch vụ du lịch tại ${selectedSite?.name}`,
            url: page.url,
            offers: services?.map((service) => {
              // Handle both rate and service formats
              const isRate = 'MinTicketBooking' in service;
              if (isRate) {
                const rate = service as any;
                const minPrice = Math.min(...rate.Services.map((s: any) => s.Price));
                return {
                  '@type': 'Offer',
                  name: rate.Name,
                  price: minPrice,
                  priceCurrency: 'VND',
                  availability: 'https://schema.org/InStock',
                };
              } else {
                const searchService = service as any;
                return {
                  '@type': 'Offer',
                  name: searchService.Name,
                  price: searchService.Total,
                  priceCurrency: 'VND',
                  availability: 'https://schema.org/InStock',
                };
              }
            }),
          })}
        </script>
      </Head>

      <PageMapBar sections={pageSections} activeId={activePageSection} groups={groupMapItems} activeGroupId={activeGroupId} minPrice={minPrice} />

      <div className="container mx-auto min-h-screen max-w-6xl pb-20">
        <div className="">
          <div className="container max-w-7xl px-4 py-4 md:py-6">
            {selectedSite && (
              <div className="mb-4">
                <DetailHeader site={selectedSite} rating={4.5} />
              </div>
            )}
            {selectedSite?.media_gallery && (
              <GalleryPreview className="mb-6" siteName={selectedSite.name} images={selectedSite.media_gallery?.images || []} />
            )}
            <PromoBanner promos={(selectedSite as any)?.promos} />
            <KeyHighlights highlights={(selectedSite as any)?.highlights} />
            {/* `inline`, not the default floating pill: this page already floats
                PageMapBar and MobileCartBottom, and a third fixed element on the
                same edge is a collision. Here the bar sits in flow under the gallery. */}
            <SearchBar sites={sites} initialData={searchParams} mobileTrigger="inline" className="md:mb-6" />
          </div>
        </div>

        {/* Results Section - 2 Column Layout */}
        <SearchResultsLayout
          className="mb-20"
          priceRail={
            <BookingPriceRail
              minPrice={minPrice}
              onBook={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            />
          }
        >
          <section id="packages" className="scroll-mt-36">
            <SectionHeader title="Các gói dịch vụ" className="mb-4" />

            <div className="mb-4">
              <SuggestDates selectedDate={searchParams.date} onDateSelect={handleDateSelect} />
            </div>

            {/* `services` and `vouchers` share Inertia's default defer group, so
                one fallback covers both and they arrive in the same request. */}
            <Deferred
              data={['services', 'vouchers']}
              fallback={
                <div className="space-y-4">
                  {/* Matches the notice's loaded height, so nothing jumps when it lands. */}
                  <Skeleton className="mb-4 h-12 w-full rounded-xl" />
                  {[1, 2].map((i) => (
                    <div key={i} className="rounded-xl border border-(--pill-border) bg-white p-5">
                      <div className="space-y-3">
                        <Skeleton className="h-5 w-3/4" />
                        <div className="flex gap-2">
                          {[1, 2, 3].map((j) => (
                            <Skeleton key={j} className="h-6 w-24 rounded-full" />
                          ))}
                        </div>
                        <div className="flex items-center justify-between pt-2">
                          <Skeleton className="h-4 w-36" />
                          <Skeleton className="h-9 w-20 rounded-lg" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              }
            >
              <>
                <SiteVoucherNotice site={vouchers?.site} siteName={selectedSite?.name} />

                <RateResults
                  rates={services as any}
                  siteName={selectedSite?.name || 'địa điểm đã chọn'}
                  searchParams={{
                    site: searchParams.site,
                    date: searchParams.date,
                  }}
                  rateVouchers={vouchers?.rates}
                  error={error}
                />
              </>
            </Deferred>
          </section>

          {/* About Section */}
          {selectedSite?.custom_content && (
            <section id="about" className="mt-10 scroll-mt-36">
              <SectionHeader title="Về dịch vụ này" className="mb-5" />
              <RichContentRenderer
                editorSerializedState={selectedSite.custom_content}
                className={cn(
                  'prose prose-gray max-w-[72ch]',
                  'prose-headings:font-semibold prose-headings:scroll-mt-24 prose-headings:text-gray-900',
                  'prose-h2:text-xl prose-h3:text-base prose-h3:mt-6',
                  'prose-p:text-gray-700 prose-p:leading-relaxed',
                  'prose-img:rounded-xl prose-img:shadow-sm prose-img:my-4',
                  'prose-ul:pl-5 prose-li:text-gray-700',
                  'prose-strong:font-semibold prose-strong:text-gray-900',
                )}
              />
            </section>
          )}
        </SearchResultsLayout>

        {/* Mobile Cart Bottom */}
        <MobileCartBottom minPrice={minPrice} />
      </div>

      <NewsletterSection />
    </WebLayout>
  );
}
